import { CampagneModel } from "@/app/entities";
import {
  DashboardHeader,
  StatCardItem,
  StatsCard,
  UiTable,
  WhiteCard,
  UiButton,
  UiBadge,
  CampagneFilterForm,
  CampagneFilterDataModel,
} from "@/components";
import { Head } from "@/components/seo";
import { useCampagne } from "@/composables/campagne";
import { UserTypes } from "@/composables/users";
import { useAuthStore, useCampagneStore } from "@/context";
import {
  EXPORT_CAMPAGNES_COLUMNS,
  ExportToExcel,
  formatAppDateTime,
} from "@/utils";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button, DropdownMenu } from "@radix-ui/themes";

export default function DashboardCampaignes() {
  const navigate = useNavigate();
  const { currentUser } = useAuthStore();
  const {
    loading,
    filterForm,
    setFilterForm,
    fetchCampagnes,
    suspendCampagne,
    updateCampagne,
    getCampagneStatusLabel,
    getCampagneStatusCode,
  } = useCampagne();

  const [downloading, setDownloading] = useState(false);
  const paginatedItems = useCampagneStore((state) => state.paginatedItems);

  useEffect(() => {
    doSearch();
  }, [filterForm]);

  const doSearch = (page?: number) => {
    fetchCampagnes(
      {
        ...filterForm,
        page: page || 0,
      },
      true
    );
  };

  const lanchCampain = (campagne: CampagneModel) => {
    updateCampagne({
      ...campagne,
      draft: false,
    });
  };

  const columns = [UserTypes.PARTICULIER, UserTypes.ENTREPRISE].includes(
    currentUser?.type as UserTypes
  )
    ? [
        {
          accessor: "nom",
          title: "Nom",
          sortable: true,
        },
        {
          accessor: "region",
          title: "Region",
        },
        {
          accessor: "dateDebut",
          title: "Date de début",
          sortable: true,
          render: (row: CampagneModel) => {
            return <span>{formatAppDateTime(row.dateDebut ?? "")}</span>;
          },
        },
        {
          accessor: "dure",
          title: "Durée",
          render: (row: CampagneModel) => {
            return <span>{row.dure ?? ""} Jour(s)</span>;
          },
        },

        {
          accessor: "actions",
          title: "Actions",
          render: (row: CampagneModel) => {
            return (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <Button variant="soft">
                    Options
                    <DropdownMenu.TriggerIcon />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  <DropdownMenu.Item
                    onClick={() =>
                      navigate(`/dashboard/campagnes/declare/${row.reference}`)
                    }
                  >
                    Déclarer
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            );
          },
        },
      ]
    : [
        {
          accessor: "nom",
          title: "Nom",
          sortable: true,
        },
        {
          accessor: "typeCampagne",
          title: "Type",
        },
        {
          accessor: "region",
          title: "Region",
        },
        {
          accessor: "dateCreation",
          title: "Date de création",
          sortable: true,
          render: (row: any) => {
            return <span>{formatAppDateTime(row.dateCreation ?? "")}</span>;
          },
        },
        {
          accessor: "ddm",
          title: "Date de modification",
          sortable: true,
          render: (row: CampagneModel) => {
            return <span>{formatAppDateTime(row.ddm ?? "")}</span>;
          },
        },
        {
          accessor: "status",
          title: "Statut",
          render: (row: CampagneModel) => {
            return (
              <UiBadge variant={getCampagneStatusCode(row)}>
                {getCampagneStatusLabel(row)}
              </UiBadge>
            );
          },
        },
        {
          accessor: "actions",
          title: "Actions",
          render: (row: CampagneModel) => {
            return (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <Button variant="soft">
                    Options
                    <DropdownMenu.TriggerIcon />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  <DropdownMenu.Item
                    onClick={() =>
                      navigate(`/dashboard/campagnes/${row.reference}`)
                    }
                  >
                    Voir détail
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onClick={() =>
                      navigate(`/dashboard/campagnes/edit/${row.reference}`)
                    }
                  >
                    Modifier
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator />

                  {!row.shelve && !row.draft && (
                    <UiButton
                      size="small"
                      color="error"
                      withAlert={true}
                      cancelLabel="Annuler"
                      okLabel="Suspendre la campagne"
                      onClick={() => suspendCampagne(row.reference)}
                    >
                      Suspendre la campagne
                    </UiButton>
                  )}

                  {row.draft && (
                    <UiButton
                      size="small"
                      withAlert={true}
                      cancelLabel="Annuler"
                      okLabel="Lancer la campagne"
                      onClick={() => lanchCampain(row)}
                    >
                      Lancer la campagne
                    </UiButton>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            );
          },
        },
      ];

  const handleFilter = (data: CampagneFilterDataModel) => {
    setFilterForm(data);
  };

  const handlePageChange = (page: number) => {
    doSearch(page - 1);
  };

  return (
    <>
      <Head title="Campagnes | Jourgui"></Head>
      <DashboardHeader
        subTitle="Bienvenue aux campagnes"
        downloading={downloading}
        onDownload={() =>
          ExportToExcel({
            columns: EXPORT_CAMPAGNES_COLUMNS,
            rows: paginatedItems.content,

            filename: "campagnes",
            onStart: () => setDownloading(true),
            onEnd: () => setDownloading(false),
          })
        }
      />

      {![UserTypes.PARTICULIER, UserTypes.ENTREPRISE].includes(
        currentUser?.type as UserTypes
      ) && (
        <div>
          <StatsCard>
            <StatCardItem
              title="Naissances"
              value="89935"
              evolution="+1.01% this week"
            />
            <StatCardItem
              title="Déclarations"
              value="23283.5"
              evolution="+1.01% this week"
            />
            <StatCardItem
              title="Animaux vaccinés"
              value="46827"
              direction="down"
              evolution="-0.09% this week"
            />
            <StatCardItem
              title="vols de bétails"
              value="23283.5"
              evolution="+1.01% this week"
            />
            <StatCardItem
              title="foyers épidémiques"
              value="124"
              evolution="+1.01% this week"
            />
            <StatCardItem
              title="bêtes identifiées"
              value="124854"
              evolution="+1.01% this week"
            />
            <StatCardItem
              title="budget dépensé"
              value="1926336562"
              evolution="+1.01% this week"
            />
          </StatsCard>
        </div>
      )}

      <div className="py-4">
        <WhiteCard
          styles={{ borderRadius: "30px" }}
          headerSlot={
            ![UserTypes.PARTICULIER, UserTypes.ENTREPRISE].includes(
              currentUser?.type as UserTypes
            ) && (
              <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
                <UiButton
                  onClick={() => navigate("/dashboard/campagnes/add")}
                  startIcon={<Plus />}
                >
                  Ajouter une campagne
                </UiButton>

                <div className="relative">
                  <CampagneFilterForm
                    defaultValue={{
                      type: "",
                      creationDate: "",
                    }}
                    onChange={handleFilter}
                  />
                </div>
              </div>
            )
          }
        >
          <div className="py-6">
            <UiTable
              columns={columns}
              rows={paginatedItems.content}
              loading={loading}
              totalElements={paginatedItems.totalElements}
              totalPages={paginatedItems.totalPages}
              pageSize={paginatedItems.size}
              page={paginatedItems.number + 1}
              onPageChange={handlePageChange}
            />
          </div>
        </WhiteCard>
      </div>
    </>
  );
}
